import { ChangeEvent, useMemo, useState } from 'react';
import {
  Button,
  InputLabel,
  FormControl,
  Select,
  TextField,
  SelectChangeEvent,
} from '@mui/material';
import { useToasts } from 'react-toast-notifications';
import styles from './ProvisionVoCAccount.module.css';
import { createProvisioningAccount } from '../api';

type AccountPropId =
  | 'account_admin_email'
  | 'account_admin_name'
  | 'organization_name'
  | 'site_url'
  | 'company_name'
  | 'size'
  | 'location';

export type AccountProps = Record<AccountPropId, string>;

const DEFAULT_DATA = {
  account_admin_email: '',
  account_admin_name: '',
  organization_name: '',
  site_url: '',
  company_name: '',
  size: '1-10',
  location: '',
};

export const ProvisionVoCAccount = () => {
  const { addToast } = useToasts();
  const [formData, setFormData] = useState<AccountProps>(DEFAULT_DATA);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleUpdateField = (event: ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [event.target?.id]: event.target.value,
    });
  };
  const handleUpdateSelect = (event: SelectChangeEvent, id: string) => {
    setFormData({
      ...formData,
      [id]: event.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await createProvisioningAccount(formData);

      if (
        response?.error_message === undefined &&
        response?.account_id !== undefined &&
        response?.user_id !== undefined
      ) {
        setFormData(DEFAULT_DATA);
        addToast('Provisioning account has been created', {
          appearance: 'success',
        });
      } else {
        addToast('There was an error while creating provisioning account', {
          appearance: 'error',
        });
      }
    } catch (error) {
      addToast(
        'There was an error while attempting the provisioning requests',
        {
          appearance: 'error',
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const createField = ({ label, id }: { label: string; id: AccountPropId }) => {
    return (
      <TextField
        label={label}
        id={id}
        name={id}
        value={formData[id]}
        onChange={handleUpdateField}
        className={styles.inputField}
        required
        data-hj-suppress
        variant="standard"
      />
    );
  };

  const isDataReadyForSubmission = useMemo(
    () => Object.values(formData).every((e) => e.length > 0),
    [formData]
  );

  return (
    <form onSubmit={handleSubmit} className={styles.form}>
      <div>
        {createField({
          label: 'Account admin email',
          id: 'account_admin_email',
        })}
        {createField({
          label: 'Account admin name',
          id: 'account_admin_name',
        })}
        {createField({ label: 'Organization name', id: 'organization_name' })}
        {createField({ label: 'Site url', id: 'site_url' })}
        {createField({ label: 'Company name', id: 'company_name' })}

        <FormControl required className={styles.inputField}>
          <InputLabel htmlFor="size">Type</InputLabel>
          <Select
            name="size"
            id="size"
            value={formData.size}
            onChange={(e) => handleUpdateSelect(e, 'size')}
            variant="standard"
            native
          >
            <option value="1-10">MICRO (1-10)</option>
            <option value="11-50">SMALL (11-50)</option>
            <option value="51-100">MEDIUM (51-100)</option>
            <option value="101-250">MEDIUM-LARGE (101-250)</option>
            <option value="251-1000">LARGE (251-1000)</option>
            <option value="1000-5000">ENTERPRISE (1000-5000)</option>
            <option value="5000-inf">MULTINATIONAL (5000-5000+)</option>
          </Select>
        </FormControl>
        {createField({ label: 'Location', id: 'location' })}
      </div>
      <Button
        variant="contained"
        color="primary"
        id="create"
        className={styles.createButton}
        type="submit"
        disabled={!isDataReadyForSubmission || isSubmitting}
      >
        Create
      </Button>
    </form>
  );
};
